/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import type { DifferencesMap, RatioType } from '../types'
import { RATIO_TYPE_OPTIONS } from './constants'

export type RatioDifferenceEntry = {
  current: number | string | null
  upstreams: Record<string, number | string | 'same'>
  confidence: Record<string, boolean>
}

export type ModelRow = {
  key: string
  model: string
  ratioTypes: Partial<Record<RatioType, RatioDifferenceEntry>>
  billingConflict: boolean
}

export type ResolutionsMap = Record<string, Record<string, number | string>>

export const RATIO_SYNC_FIELDS: RatioType[] = [
  'model_ratio',
  'completion_ratio',
  'cache_ratio',
  'create_cache_ratio',
  'image_ratio',
  'audio_ratio',
  'audio_completion_ratio',
]

export const SYNC_FIELD_ORDER: RatioType[] = [
  ...RATIO_SYNC_FIELDS,
  'model_price',
  'billing_mode',
  'billing_expr',
]

export const NUMERIC_SYNC_FIELDS = new Set<string>([
  ...RATIO_SYNC_FIELDS,
  'model_price',
])

export function getSyncFieldLabel(
  ratioType: string,
  t: (key: string) => string
): string {
  const opt = RATIO_TYPE_OPTIONS.find((o) => o.value === ratioType)
  if (opt) return t(opt.label)
  return ratioType
}

export function getOrderedRatioTypes(
  ratioTypes: Partial<Record<RatioType, RatioDifferenceEntry>>,
  filter?: string
): RatioType[] {
  const keys = Object.keys(ratioTypes) as RatioType[]
  const ordered = [
    ...SYNC_FIELD_ORDER.filter((f) => keys.includes(f)),
    ...keys.filter((f) => !SYNC_FIELD_ORDER.includes(f)),
  ]
  if (!filter || filter === '__all__') return ordered
  return ordered.filter((f) => f === filter)
}

export function getPreferredSyncField(
  ratioTypes: Partial<Record<RatioType, RatioDifferenceEntry>>,
  ratioType: RatioType,
  sourceName: string
): RatioType {
  const exprValue = ratioTypes.billing_expr?.upstreams?.[sourceName]
  if (
    ratioType !== 'billing_expr' &&
    exprValue !== null &&
    exprValue !== undefined &&
    exprValue !== 'same'
  ) {
    return 'billing_expr'
  }
  return ratioType
}

export function isSelectableUpstreamValue(
  value: number | string | 'same' | null | undefined
): boolean {
  return value !== null && value !== undefined && value !== 'same'
}

// ---------------------------------------------------------------------------
// Shared sync plumbing (used by both the manual UpstreamRatioSync panel and the
// one-click vendor price dialog). Keep these pure so both call sites write the
// exact same option payloads.
// ---------------------------------------------------------------------------

export type BillingCategory = 'price' | 'ratio' | 'tiered'

export function getBillingCategory(ratioType: string): BillingCategory {
  if (ratioType === 'model_price') return 'price'
  if (ratioType === 'billing_mode' || ratioType === 'billing_expr')
    return 'tiered'
  return 'ratio'
}

/** snake_case sync field -> the key used inside the options table. */
export function optionKeyBySyncField(ratioType: string): string {
  const explicit: Record<string, string> = {
    billing_mode: 'billing_setting.billing_mode',
    billing_expr: 'billing_setting.billing_expr',
  }
  if (explicit[ratioType]) return explicit[ratioType]
  return ratioType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
}

export function parseJsonRecord<T>(
  raw: string | undefined | null
): Record<string, T> {
  try {
    return JSON.parse(raw || '{}') as Record<string, T>
  } catch {
    return {}
  }
}

export function deleteResolutionField(
  res: ResolutionsMap,
  model: string,
  ratioType: string
): ResolutionsMap {
  if (!res[model]) return res
  const newModelRes = { ...res[model] }
  delete newModelRes[ratioType]
  if (ratioType === 'billing_expr') delete newModelRes['billing_mode']
  if (ratioType === 'billing_mode') delete newModelRes['billing_expr']
  const next = { ...res }
  if (Object.keys(newModelRes).length === 0) {
    delete next[model]
  } else {
    next[model] = newModelRes
  }
  return next
}

/**
 * Merge the current options maps with the user's resolutions and turn them into
 * the `[{key, value}]` payload accepted by `PUT /api/option/`.
 *
 * `model_price` and the ratio fields are mutually exclusive per model, so
 * choosing one clears the other (mirrors the historical inline behaviour).
 */
export function buildSyncUpdates(
  currentRatios: Record<string, Record<string, number | string>>,
  resolutions: ResolutionsMap
): Array<{ key: string; value: string }> {
  const finalRatios: Record<string, Record<string, number | string>> = {}
  Object.entries(currentRatios).forEach(([key, value]) => {
    finalRatios[key] = { ...value }
  })

  Object.entries(resolutions).forEach(([model, ratios]) => {
    const selectedTypes = Object.keys(ratios)
    const hasPrice = selectedTypes.includes('model_price')
    const hasRatio = selectedTypes.some((rt) =>
      RATIO_SYNC_FIELDS.includes(rt as RatioType)
    )

    if (hasPrice) {
      RATIO_SYNC_FIELDS.forEach((rt) => {
        const key = optionKeyBySyncField(rt)
        if (finalRatios[key]) delete finalRatios[key][model]
      })
    }
    if (hasRatio && finalRatios.ModelPrice) {
      delete finalRatios.ModelPrice[model]
    }

    Object.entries(ratios).forEach(([ratioType, value]) => {
      const optionKey = optionKeyBySyncField(ratioType)
      if (!finalRatios[optionKey]) finalRatios[optionKey] = {}
      finalRatios[optionKey][model] = NUMERIC_SYNC_FIELDS.has(ratioType)
        ? Number(value)
        : value
    })
  })

  return Object.entries(finalRatios).map(([key, value]) => ({
    key,
    value: JSON.stringify(value, null, 2),
  }))
}

/**
 * Auto-select every selectable value an upstream provides.
 *
 * Used by the one-click vendor dialog: official price pages only ever publish
 * `model_ratio` / `completion_ratio` / `cache_ratio`, so there is no
 * price-vs-ratio conflict to resolve — but the guard is kept for safety.
 *
 * `upstream` is the key inside `differences[model][ratioType].upstreams` — the
 * backend emits `"<channel name>(<channel id>)"`. Pass `undefined` (the vendor
 * dialog does) to just take the first selectable source, which is correct when
 * exactly one upstream was queried and avoids duplicating the backend's naming
 * rule on the client.
 */
export function buildResolutionsFromUpstream(
  differences: DifferencesMap,
  upstream?: string
): ResolutionsMap {
  const resolutions: ResolutionsMap = {}

  Object.entries(differences).forEach(([model, ratioTypes]) => {
    const perModel: Record<string, number | string> = {}
    // `ratio` wins over `price` when a model somehow has both.
    let category: BillingCategory | null = null

    Object.entries(ratioTypes).forEach(([ratioType, entry]) => {
      const upMap = entry?.upstreams
      if (!upMap) return

      const sourceName =
        upstream && upstream in upMap
          ? upstream
          : Object.keys(upMap).find((name) =>
              isSelectableUpstreamValue(upMap[name])
            )
      if (!sourceName) return

      const value = upMap[sourceName]
      if (!isSelectableUpstreamValue(value)) return

      // Skip a ratio field when the same upstream publishes a tiered
      // expression instead — mirrors the manual picker's precedence.
      const preferredType = getPreferredSyncField(
        ratioTypes,
        ratioType as RatioType,
        sourceName
      )
      if (preferredType !== ratioType) return

      const cat = getBillingCategory(ratioType)
      if (cat === 'price' && category === 'ratio') return
      if (cat === 'ratio' && category === 'price') {
        RATIO_SYNC_FIELDS.forEach((rt) => delete perModel[rt])
      }
      if (cat !== 'tiered') category = cat

      perModel[ratioType] = value as number | string
    })

    if (Object.keys(perModel).length > 0) resolutions[model] = perModel
  })

  return resolutions
}
